import styled from 'styled-components'
import type { FunctionComponent } from 'react'
import useWindowWidth from '../../hooks/useWindowWidth'
import { Breakpoint } from '../../hooks/useBreakpoint'

const Box = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 2rem;
  width: 100%;
`
const BoxHorizontal = styled.div`
  display: flex;
  flex-direction: row;
  margin: 0.5rem 0 1.5rem;
`

export const Skeleton = styled.span`
  display: inline-block;
  height: 1.125rem;
  position: relative;
  overflow: hidden;
  background-color: #dddbdd;
  margin-bottom: 0.75rem;

  &::after {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    transform: translateX(-100%);
    background-image: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0,
      rgba(255, 255, 255, 0.2) 20%,
      rgba(255, 255, 255, 0.5) 60%,
      rgba(255, 255, 255, 0)
    );
    animation: shimmer 2s infinite;
    content: '';
  }
`

interface Props {
  height: string
  marginRight?: string
}

export const SkeletonFlexHeight = styled.span<Props>`
  display: inline-block;
  height: ${(p) => p.height};
  position: relative;
  overflow: hidden;
  background-color: #dddbdd;
  margin-bottom: 0.75rem;
  margin-right: ${(p) => p.marginRight};

  &::after {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    transform: translateX(-100%);
    background-image: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0,
      rgba(255, 255, 255, 0.2) 20%,
      rgba(255, 255, 255, 0.5) 60%,
      rgba(255, 255, 255, 0)
    );
    animation: shimmer 2s infinite;
    content: '';
  }
`

const SkeletonWrapper = styled.div`
  margin-top: 1rem;
`

const SkeletonContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  @media screen and (min-width: 48rem) {
    flex-direction: row;
  }
`

const SkeletonColumn = styled.div`
  flex-basis: 100%;
  @media screen and (min-width: 48rem) {
    flex-basis: 50%;
  }
  padding-right: 1rem;
`

export const LinkListSkeleton: FunctionComponent<{ limit?: number }> = ({ limit }) => {
  let sizes = [65, 62, 85, 78, 98, 68, 65, 62, 85, 78, 98, 68, 98, 68, 65, 62, 85, 78, 85, 78]

  if (limit) {
    sizes = sizes.slice(0, limit)
  }

  return (
    <SkeletonWrapper data-testid="skeletonLinkList">
      <Box>
        {sizes.map((size, index) => (
          <Skeleton key={`skeleton-${String(index)}`} style={{ width: `${size}%` }} />
        ))}
      </Box>
    </SkeletonWrapper>
  )
}

export const MultipleComponentsSkeleton = () => (
  <SkeletonWrapper data-testid="skeletonLoader">
    <Skeleton style={{ width: '80%', fontSize: '2rem' }} />
    <Box>
      <Skeleton style={{ width: '40%' }} />
      <Skeleton style={{ width: '36%' }} />
      <Skeleton style={{ width: '68%' }} />
      <Skeleton style={{ width: '50%' }} />
      <Skeleton style={{ width: '32%' }} />
    </Box>
    <Skeleton style={{ width: '60%', fontSize: '2rem' }} />
    <Box>
      <Skeleton style={{ width: '36%' }} />
      <Skeleton style={{ width: '32%' }} />
      <Skeleton style={{ width: '50%' }} />
      <Skeleton style={{ width: '40%' }} />
      <Skeleton style={{ width: '68%' }} />
    </Box>
    <Skeleton style={{ width: '100%', fontSize: '2rem' }} />
    <Box>
      <Skeleton style={{ width: '50%' }} />
      <Skeleton style={{ width: '40%' }} />
      <Skeleton style={{ width: '68%' }} />
      <Skeleton style={{ width: '36%' }} />
      <Skeleton style={{ width: '32%' }} />
    </Box>
  </SkeletonWrapper>
)

export const PanoramaSkeleton = () => {
  const width = useWindowWidth()
  return (
    <SkeletonFlexHeight
      data-testid="panoramaPreview-loading"
      style={{ width: '100%', marginBottom: 0 }}
      height={width >= Breakpoint.Md.valueOf() ? '175px' : '150px'}
    />
  )
}

export const AdresSkeleton = () => (
  <Box data-testid="addressSkeleton">
    <SkeletonFlexHeight style={{ width: '20%' }} height="1.2rem" />
    <SkeletonFlexHeight style={{ width: '40%' }} height="1.5rem" />
    <SkeletonFlexHeight style={{ width: '45%' }} height="1.12rem" />
  </Box>
)

export const EmptyTabSkeleton = () => (
  <SkeletonContainer data-testid="tabSkeleton">
    <SkeletonColumn>
      <SkeletonWrapper data-testid="skeletonLoader">
        <Skeleton style={{ width: '80%', fontSize: '2rem' }} />
        <Box>
          <SkeletonFlexHeight style={{ width: '100%' }} height="4.5rem" />
          <SkeletonFlexHeight style={{ width: '100%' }} height="4.5rem" />
        </Box>
      </SkeletonWrapper>
    </SkeletonColumn>
    <SkeletonColumn>
      <SkeletonWrapper data-testid="skeletonLoader">
        <Skeleton style={{ width: '80%', fontSize: '4rem' }} />
        <Box>
          <SkeletonFlexHeight style={{ width: '100%' }} height="4.5rem" />
          <SkeletonFlexHeight style={{ width: '100%' }} height="4.5rem" />
        </Box>
      </SkeletonWrapper>
    </SkeletonColumn>
  </SkeletonContainer>
)

export const HighlightsSkeleton = () => (
  <BoxHorizontal data-testid="highlightsSkeleton">
    <SkeletonFlexHeight style={{ width: '15%' }} height="2rem" marginRight="1rem" />
    <SkeletonFlexHeight style={{ width: '15%' }} height="2rem" marginRight="1rem" />
    <SkeletonFlexHeight style={{ width: '15%' }} height="2rem" marginRight="1rem" />
    <SkeletonFlexHeight style={{ width: '15%' }} height="2rem" marginRight="1rem" />
  </BoxHorizontal>
)

export const SquareSkeleton = () => <SkeletonFlexHeight style={{ width: '100%' }} height="100%" />
